import React, { useState } from "react"
import SlotTypeBreakdown from "./SlotTypeBreakdown" // Without curly braces
import ExpectedPatientsBarchart from "./ExpectedPatientsBarchart" // Without curly braces
import useFetchTemplates from "../../hooks/useFetchTemplates"
import { PageLoader } from "../Authentication/page-loader"
import ErrorPage from "../../pages/ErrorPage"

const bins = [
	"0-1 hour",
	"1-2 hours",
	"2-3 hours",
	"3-4 hours",
	"4-5 hours",
	"5-6 hours",
	"6+ hours"
]
const hours = [0, 1, 2, 3, 4, 5, 6, Infinity]

const getGradientColor = (value, column) => {
	if (value === 0) return "bg-white" // Return 'bg-white' instead of just 'white'
	switch (column) {
		case bins[0]:
			return "bg-[rgba(173,137,255,0.5)]" // Light purple
		case bins[1]:
			return "bg-[rgba(122,94,255,0.7)]" // Slightly darker purple
		case bins[2]:
			return "bg-[rgba(97,69,255,0.8)]" // Darker purple
		case bins[3]:
			return "bg-[rgba(65,44,165,0.9)]" // Even darker purple
		case bins[4]:
			return "bg-[rgba(51,34,132,1)]" // Darkest purple
		case bins[5]:
			return "bg-[rgba(34,21,85,1)]" // Darkest purple
		case bins[6]:
			return "bg-[rgba(21,12,51,1)]" // Darkest purple
		default:
			return "bg-white" // Default to white
	}
}

// Return appropriate text color based on background color
const getTextColor = value => (value !== 0 ? "text-white" : "text-black")

const calculateStats = daySchedule => {
	const maxExpectedPatients = Math.max(...daySchedule.map(row => row.expectedPatients))
	const typeBreakdown = {
		[bins[0]]: calculateColumnAggregate(daySchedule, bins[0]),
		[bins[1]]: calculateColumnAggregate(daySchedule, bins[1]),
		[bins[2]]: calculateColumnAggregate(daySchedule, bins[2]),
		[bins[3]]: calculateColumnAggregate(daySchedule, bins[3]),
		[bins[4]]: calculateColumnAggregate(daySchedule, bins[4]),
		[bins[5]]: calculateColumnAggregate(daySchedule, bins[5]),
		[bins[6]]: calculateColumnAggregate(daySchedule, bins[6])
	}

	const totalSlots = Object.values(typeBreakdown).reduce((sum, value) => sum + value, 0)
	const totalDuration = daySchedule.reduce(
		(sum, row) =>
			sum +
			(row[bins[0]] * hours[1] +
				row[bins[1]] * hours[2] +
				row[bins[2]] * hours[3] +
				row[bins[3]] * hours[4] +
				row[bins[4]] * hours[5] +
				row[bins[5]] * hours[6] +
				row[bins[6]] * hours[7]),
		0
	)
	const averageDuration = totalSlots ? (totalDuration / totalSlots).toFixed(2) : 0

	const expectedPatientsData = daySchedule.map(row => ({
		time: row.time,
		expectedPatients: row.expectedPatients
	}))

	const concurrentPatientsByTime = daySchedule.reduce((acc, row) => {
		const currentTime = row.time
		acc[currentTime] = (acc[currentTime] || 0) + row.expectedPatients
		return acc
	}, {})

	const maxConcurrentPatientsTime = Object.keys(concurrentPatientsByTime).reduce(
		(maxTime, time) =>
			concurrentPatientsByTime[time] > (concurrentPatientsByTime[maxTime] || 0)
				? time
				: maxTime,
		null
	)

	return {
		totalSlots,
		maxExpectedPatients,
		typeBreakdown,
		averageDuration,
		maxConcurrentPatientsTime,
		expectedPatientsData
	}
}

// Render the stat cards with responsive design
const renderStatCards = stats => {
	return (
		<div className="grid grid-cols-1 xl:grid-cols-5 gap-6 p-4 mb-6">
			<div className="xl:col-span-2">
				<SlotTypeBreakdown
					key={`${JSON.stringify(stats.typeBreakdown)}-${bins.join("-")}-${hours.join("-")}`}
					typeBreakdown={stats.typeBreakdown}
					slots={bins}
					hours={hours}
				/>
			</div>
			<div className="xl:col-span-3 sm:col-span-1">
				<ExpectedPatientsBarchart expectedPatientsData={stats.expectedPatientsData} />
			</div>
		</div>
	)
}

// Aggregate stats for each column
const calculateColumnAggregate = (schedule, column) => {
	return schedule.reduce((sum, row) => sum + (row[column] || 0), 0)
}

const extractHour = time => {
	const [hour] = time.split(":")
	return parseInt(hour, 10)
}

function convertTo12HourFormat(time) {
	let [hour, minute] = time.split(":").map(Number)
	const ampm = hour >= 12 ? "PM" : "AM"
	hour = hour % 12 || 12
	return `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`
}

const ScheduleTabs = () => {
	const [selectedIndex, setSelectedIndex] = useState(0)

	const { templatesData, templatesStatus } = useFetchTemplates()
	/* TODO: we need to change this page if we use different slot lengths. */

	const weekdayOrder = [
		"Monday",
		"Tuesday",
		"Wednesday",
		"Thursday",
		"Friday",
		"Saturday",
		"Sunday"
	]

	const tabs = templatesData
		? templatesData
				.map((template, index) => ({
					name: template.day_of_week,
					index
				}))
				.sort((a, b) => {
					const weekdayIndexA = weekdayOrder.indexOf(a.name)
					const weekdayIndexB = weekdayOrder.indexOf(b.name)

					// Check if both are weekdays
					if (weekdayIndexA !== -1 && weekdayIndexB !== -1) {
						return weekdayIndexA - weekdayIndexB
					}

					// If a is a weekday and b is not, a comes first
					if (weekdayIndexA !== -1) return -1
					// If b is a weekday and a is not, b comes first
					if (weekdayIndexB !== -1) return 1

					// If both are holidays, strip the word "Holiday" and sort them according to weekday index
					return (
						weekdayOrder.indexOf(a.name.replace("Holiday", "").trim()) -
						weekdayOrder.indexOf(b.name.replace("Holiday", "").trim())
					)
				})
		: []

	const renderTabContent = () => {
		if (templatesStatus === "loading") {
			return <PageLoader />
		}
		if (templatesStatus === "failed") {
			return <ErrorPage />
		}
		if (!templatesData || templatesData.length === 0) {
			return <div>No data available.</div>
		}

		const dayTemplate = templatesData[selectedIndex]
		if (!dayTemplate) {
			return <div>No schedule available for this day.</div>
		}

		const daySchedule = dayTemplate.schedule
		const stats = calculateStats(daySchedule)

		// Calculate aggregate stats for each column
		const aggregate_bins = [
			calculateColumnAggregate(daySchedule, bins[0]),
			calculateColumnAggregate(daySchedule, bins[1]),
			calculateColumnAggregate(daySchedule, bins[2]),
			calculateColumnAggregate(daySchedule, bins[3]),
			calculateColumnAggregate(daySchedule, bins[4]),
			calculateColumnAggregate(daySchedule, bins[5]),
			calculateColumnAggregate(daySchedule, bins[6])
		]

		let lastHour = -1

		return (
			<div className="p-4 border rounded-lg shadow-lg bg-white">
				{renderStatCards(stats)}
				<div className="overflow-x-auto">
					<table className="min-w-full divide-y divide-gray-200">
						<thead key={`${JSON.stringify(bins)}-${JSON.stringify(hours)}`}>
							<tr key={`${JSON.stringify(bins)}-${JSON.stringify(hours)}`}>
								<th className="px-4 py-2 text-center text-gray-700">Time</th>
								{bins.map((bin, index) => (
									<th key={index} className="px-4 py-2 text-center text-gray-700">
										{bin.split(" ")[0]} hrs
									</th>
								))}
								<th className="px-4 py-2 text-center text-gray-700">Appt Starts</th>
								<th className="px-4 py-2 text-center text-gray-700">
									Appt Finishes
								</th>
								<th className="px-4 py-2 text-center text-gray-700">
									Expected Patients
								</th>
								<th className="px-4 py-2 text-center text-gray-700">
									Total Starts/hr
								</th>
							</tr>
						</thead>
						<tbody>
							{daySchedule.map((row, index) => {
								const currentHour = extractHour(row.time)
								const showTotalStarts = currentHour !== lastHour
								lastHour = currentHour

								return (
									<tr key={`${row * 1000}-${index}`}>
										<td className="px-4 py-2 text-center">
											{convertTo12HourFormat(row.time)}
										</td>
										{bins.map((bin, index) => (
											<td
												className={`px-4 py-2 text-center ${getGradientColor(row[bin], bin)} ${getTextColor(row[bin])}`}
											>
												{row[bin]}
											</td>
										))}
										<td className="px-4 py-2 text-center">{row.appStarts}</td>
										<td className="px-4 py-2 text-center">{row.appFinishes}</td>
										<td className="px-4 py-2 text-center">
											{row.expectedPatients}
										</td>

										{showTotalStarts && (
											<td
												className="px-4 py-2 text-center font-bold"
												rowSpan={
													daySchedule.filter(
														r => extractHour(r.time) === currentHour
													).length
												}
											>
												{row.totalStarts}
											</td>
										)}
									</tr>
								)
							})}
							{/* Cumulative Stats Row */}
							<tr>
								<td className="px-4 py-2 text-center font-bold">Totals</td>
								{aggregate_bins.map((bin, index) => (
									<td className="px-4 py-2 text-center font-bold">{bin}</td>
								))}
								<td colSpan="1" className="px-4 py-2 text-center font-bold">
									{calculateColumnAggregate(daySchedule, "appStarts")}
								</td>
								<td colSpan="1" className="px-4 py-2 text-center font-bold">
									{calculateColumnAggregate(daySchedule, "appFinishes")}
								</td>
								<td colSpan="2" className="px-4 py-2 text-center font-bold">
									-
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		)
	}

	return (
		<div className="tabsContainer">
			<div className="sm:hidden print:hidden">
				<label htmlFor="tabs" className="sr-only">
					Select a tab
				</label>
				<select
					id="tabs"
					name="tabs"
					className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
					value={tabs[selectedIndex]?.name || ""}
					onChange={e =>
						setSelectedIndex(tabs.find(tab => tab.name === e.target.value)?.index || 0)
					}
				>
					{tabs.map(tab => (
						<option key={tab.name}>{tab.name}</option>
					))}
				</select>
			</div>

			<div className="hidden sm:block print:hidden">
				<div className="max-w-full border-b border-gray-200">
					<nav
						className="-mb-px flex space-x-8 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
						aria-label="Tabs"
					>
						{tabs.map(tab => (
							<button
								key={tab.name}
								onClick={() => setSelectedIndex(tab.index)}
								className={`group inline-flex items-center px-1 py-4 text-base font-medium whitespace-nowrap
								${
									selectedIndex === tab.index
										? "border-b-2 border-customBlue text-customBlue"
										: "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
								}`}
								aria-current={selectedIndex === tab.index ? "page" : undefined}
							>
								<span>{tab.name}</span>
							</button>
						))}
					</nav>
				</div>
			</div>

			<div className="tabContent mt-4 print:block">{renderTabContent()}</div>
		</div>
	)
}

export default ScheduleTabs
